<script setup lang="ts">
/**
 * CV languages page body: three stat cards built from the language list, the
 * full list as chips, and the two ways forward. The page title band comes from
 * MarketingFeaturePage.
 */
import { defaultLocalePath } from '~/constants/appLocale'
import type { CVLanguage } from '~/constants/cvLanguages'
import { SUPPORTED_CV_LANGUAGES } from '~/constants/cvLanguages'

const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const totalLanguages = SUPPORTED_CV_LANGUAGES.length
const rtlLanguageCount = SUPPORTED_CV_LANGUAGES.filter(language => language.rtl).length

const remainingLanguages = computed<CVLanguage[]>(() => {
  return [...SUPPORTED_CV_LANGUAGES].sort((a, b) => a.nativeName.localeCompare(b.nativeName))
})

const statCards = computed(() => [
  { id: 'languages', icon: 'bi-translate', value: String(totalLanguages) },
  { id: 'variations', icon: 'bi-layers', value: t('homepage.languageSupport.stats.variations.value') },
  { id: 'rtl', icon: 'bi-text-right', value: String(rtlLanguageCount) },
])

const router = useRouter()
const route = useRoute()
const localize = useLocalizedPath()

// Navigate to feature section
const handleFeatureClick = (hashFragment: string) => {
  // Navigate to section
  if (defaultLocalePath(route.path) !== '/') {
    router.push(localize('/' + hashFragment))
  } else {
    const targetElement = document.querySelector(hashFragment)
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      window.history.pushState(null, '', route.path + hashFragment)
    }
  }
}
</script>

<template>
  <section class="sofax surface-alt relative overflow-hidden">
    <div class="section-space">
      <div class="shell relative z-10">
        <SofaxSectionHeading
          :eyebrow="t('homepage.languageSupport.badge')"
          :title="t('homepage.languageSupport.title', { count: totalLanguages })"
          :text="t('homepage.languageSupport.subtitle', { count: totalLanguages })"
        />

        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- Stat cards in the service-card style -->
          <ul class="grid grid-cols-1 gap-6 md:grid-cols-3">
            <li
              v-for="card in statCards"
              :key="card.id"
              class="h-full rounded-[0.625rem] bg-white p-[1.875rem] transition-all duration-300 hover:shadow-lift dark:bg-gray-800"
            >
              <div class="flex items-start justify-between gap-4">
                <div class="display-heading display-heading-2">{{ card.value }}</div>
                <SofaxIconTile :icon="card.icon" round size="sm" />
              </div>
              <div class="display-heading display-heading-5 mb-3 mt-6">
                {{ t(`homepage.languageSupport.stats.${card.id}.label`) }}
              </div>
              <p class="text-base">{{ t(`homepage.languageSupport.stats.${card.id}.description`) }}</p>
            </li>
          </ul>

          <!-- Every supported language as a chip -->
          <div class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800 md:p-10">
            <h3>{{ t('homepage.languageSupport.allLanguages.title') }}</h3>
            <p class="mt-3">{{ t('homepage.languageSupport.allLanguages.subtitle') }}</p>

            <ul class="mt-8 flex max-h-80 flex-wrap gap-3 overflow-y-auto pr-2">
              <li v-for="language in remainingLanguages" :key="language.code" class="chip !px-4 !py-1.5 !text-sm">
                <span aria-hidden="true" class="mr-2">{{ language.flag }}</span>
                <span>{{ language.nativeName }}</span>
              </li>
            </ul>
          </div>

          <div class="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <SofaxBtn
              :to="isAuthenticated ? '/cv' : '/registration'"
              :label="t('homepage.languageSupport.cta.primary')"
              variant="accent"
            />
            <SofaxBtn
              :label="t('homepage.languageSupport.cta.secondary')"
              variant="outline"
              @click="handleFeatureClick('#templates')"
            />
          </div>
        </div>
      </div>

      <img
        src="/sofax/hero-1-element-1.svg"
        alt=""
        aria-hidden="true"
        width="291"
        height="200"
        class="pointer-events-none absolute -left-16 top-32 -z-10 hidden opacity-70 xl:inline-block"
      >
    </div>
  </section>
</template>
